import { useContext, useEffect, useState } from "react";
import { Button, Input, Select, Table, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar, sarToMinorUnits } from "../../../../utils/billingMoney";
import { createCouponAPI, listCouponsAPI, updateCouponAPI } from "../../../../utils/api/billing";
import { useBillingAdminText } from "../billingAdminI18n";

const emptyForm = { code: "", type: "percent", value: "" };

const CouponsPanel = ({ canManage, onChanged }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ ...emptyForm });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listCouponsAPI(token);
      setCoupons(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateValue = (type, value) => {
    const numericValue = Number(type === "amount" ? sarToMinorUnits(value) : value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      setError(text.settings.couponModal.valueInvalid);
      return null;
    }
    if (type === "percent" && numericValue > 100) {
      setError(text.settings.couponModal.percentBounds);
      return null;
    }
    return numericValue;
  };

  const startEdit = (coupon) => {
    setEditing(coupon._id);
    setForm({ code: coupon.code, type: coupon.type, value: coupon.type === "amount" ? (coupon.value / 100).toFixed(2) : String(coupon.value) });
    setError("");
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setError("");
  };

  const create = async () => {
    if (!can("billing.admin.settings.manage")) return;
    if (!form.code.trim()) {
      setError(text.settings.couponModal.codeRequired);
      return;
    }
    const numericValue = validateValue(form.type, form.value);
    if (numericValue === null) return;
    setSaving(true);
    setError("");
    try {
      await createCouponAPI(token, { code: form.code, type: form.type, value: numericValue });
      setForm({ ...emptyForm });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!can("billing.admin.settings.manage")) return;
    const numericValue = validateValue(form.type, form.value);
    if (numericValue === null) return;
    setSaving(true);
    setError("");
    try {
      await updateCouponAPI(token, editing, { value: numericValue });
      cancelEdit();
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon) => {
    if (!can("billing.admin.settings.manage")) return;
    setBusyId(coupon._id);
    setError("");
    try {
      await updateCouponAPI(token, coupon._id, { active: !coupon.active });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId("");
    }
  };

  const columns = [
    { key: "code", label: text.settings.couponModal.code, render: (coupon) => <strong>{coupon.code}</strong> },
    { key: "type", label: text.settings.couponModal.type, render: (coupon) => (coupon.type === "percent" ? text.settings.couponModal.typePercent : text.settings.couponModal.typeAmount) },
    { key: "value", label: text.settings.couponModal.value, render: (coupon) => (coupon.type === "percent" ? `${coupon.value}%` : formatSar(coupon.value, lang)) },
    { key: "status", label: text.common.status, render: (coupon) => <Tags variant={coupon.active ? "success" : "neutral"}>{coupon.active ? text.settings.couponModal.active : text.settings.couponModal.inactive}</Tags> },
    {
      key: "actions",
      label: text.common.actions,
      render: (coupon) =>
        canManage ? (
          <div className="billing-plan-actions">
            <Button size="sm" appearance="outline" onClick={() => startEdit(coupon)}>
              {text.common.edit}
            </Button>
            <Button size="sm" appearance="soft" loading={busyId === coupon._id} onClick={() => toggleActive(coupon)}>
              {coupon.active ? text.settings.couponModal.deactivate : text.settings.couponModal.activate}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="billing-settings-panel">
      <p className="billing-settings-panel-note">{text.settings.couponModal.description}</p>
      {error && <div className="billing-error">{error}</div>}
      {canManage && (
        <div className="billing-plan-modal-form">
          <strong>{editing ? text.settings.couponModal.editTitle : text.settings.couponModal.addNew}</strong>
          <div className="billing-form-grid">
            <Input
              label={text.settings.couponModal.code}
              value={form.code}
              disabled={Boolean(editing)}
              onChange={(event) => setForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))}
            />
            <Select
              label={text.settings.couponModal.type}
              value={form.type}
              disabled={Boolean(editing)}
              onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
              options={[
                { value: "percent", label: text.settings.couponModal.typePercent },
                { value: "amount", label: text.settings.couponModal.typeAmount },
              ]}
            />
            <Input label={text.settings.couponModal.value} value={form.value} onChange={(event) => setForm((current) => ({ ...current, value: event.target.value }))} />
          </div>
          <div className="billing-modal-actions">
            <Button appearance="filled" loading={saving} onClick={editing ? saveEdit : create}>
              {editing ? text.settings.couponModal.save : text.settings.couponModal.create}
            </Button>
            {editing && (
              <Button appearance="ghost" onClick={cancelEdit}>
                {text.common.cancel}
              </Button>
            )}
          </div>
        </div>
      )}

      <Table columns={columns} data={coupons} loading={loading} emptyText={text.settings.couponModal.empty} />
    </div>
  );
};

export default CouponsPanel;
